import React, { useState } from "react";
import { Link, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { AdminResourceDeleteModal, CourseBadgeStatus, HeaderWithBreadcrumbs, PageMain } from "components/molecules";
import { routes } from "consts";
import { CourseGroupApi, coursegroupsApi, useCourseGroup, useCourseGroupCourses } from "api/coursegroups";
import { PaziaLoader } from "components/atoms";
import { TanTableClient, TanTableColumn } from "components/molecules";
import { formatDate, toMoneyFormat } from "utils";
import { CourseGroupPreviewDataFrom } from "./CourseGroupPreviewDataFrom";
import { removeTrailingSlash } from "utils/urlUtils";
import { PaziaBox, PaziaTabs } from "components/atoms";
import { CourseGroupBaseForm, CourseGroupFormSchema } from "./CourseGroupBaseForm";
import { getApiErrorMessage, useToaster } from "utils";
import { COURSE_GROUP_FIELD_TYPE, CourseData, coursesApi } from "api";
import { CourseFlags } from "pages/Courses/CourseFlags";
import { CoursesTableActions } from "pages/Courses/CoursesPage";
import { COURSE_STATUS_OPTIONS, CourseStatus } from "consts/courseType";
import { DuplicateCourseModal } from "components/templates";
import { breadcrumbs } from "pages/breadcrumbs";
import { CourseGroupContactPersons } from "./CourseGroupContactPersons";
import { ProgramEditor } from "./ProgramEditor";
import { FaqEditor } from "./FaqEditor";
import { CourseGroupAttributesTab } from "./CourseGroupAttributesTab";
import { CourseGroupWaitlistTab } from "./CourseGroupWaitlistTab";

export const CourseGroupEditPage = () => {
  const { courseGroupId } = useParams();
  const { isLoading, courseGroup, invalidate } = useCourseGroup(courseGroupId);

  if (isLoading) return <PaziaLoader position="center" />;
  if (!courseGroup) return null;
  return (
    <PageMain>
      <HeaderWithBreadcrumbs
        breadcrumbs={breadcrumbs.COURSE_GROUP_EDIT(courseGroup.name)}
        headerText={courseGroup.name}
      />
      <PaziaBox>
        <Tabs courseGroupId={courseGroupId!} />
        <Routes>
          <Route path={""} element={<CourseGroupBaseTab courseGroup={courseGroup} invalidate={invalidate} />} />
          <Route
            path={"preview"}
            element={
              <CourseGroupPreviewDataFrom courseGroup={courseGroup} fieldType={COURSE_GROUP_FIELD_TYPE.DESCRIPTION} />
            }
          />
          <Route path={"courses"} element={<CourseGroupCoursesTab courseGroup={courseGroup} />} />
          <Route path={"contact"} element={<CourseGroupContactPersons courseGroup={courseGroup} />} />
          <Route
            path={"detailed-info"}
            element={
              <CourseGroupPreviewDataFrom
                courseGroup={courseGroup}
                fieldType={COURSE_GROUP_FIELD_TYPE.DETAILED_INFORMATION}
              />
            }
          />

          <Route path={"agenda"} element={<ProgramEditor courseGroup={courseGroup} />} />
          <Route path={"faq"} element={<FaqEditor courseGroup={courseGroup} />} />
          <Route path={"attributes"} element={<CourseGroupAttributesTab courseGroupId={courseGroupId!} />} />
          <Route path={"waitlist"} element={<CourseGroupWaitlistTab courseGroupId={courseGroupId!} />} />
        </Routes>
      </PaziaBox>
    </PageMain>
  );
};

const CourseGroupCoursesTab = ({ courseGroup }: { courseGroup: CourseGroupApi }) => {
  const { isLoading, courses, invalidate } = useCourseGroupCourses(courseGroup.id.toString());
  const [duplicateCourseId, setDuplicateCourseId] = useState<number | null>(null);
  const navigate = useNavigate();
  const filterStorageKey = `course-group-courses-filter-${courseGroup.id}`;

  const columns = React.useMemo<TanTableColumn<CourseData>[]>(
    () => [
      {
        id: "name",
        header: "Edycja",
        accessorKey: "name",
        cell: (info) => (
          <span className="flex flex-col">
            <span>
              {info.row.original.variant && <span className="font-semibold">Edycja: </span>}
              {info.row.original.edition}
            </span>
            {info.row.original.variant && (
              <span>
                <span className="font-semibold">Wariant: </span>
                {info.row.original.variant}
              </span>
            )}
          </span>
        ),
      },
      {
        id: "dateOfCourse",
        accessorKey: "dateOfCourse",
        header: "Data",
        size: 150,
        enableColumnFilter: false,
        cell: (info) => (
          <span>
            {formatDate(info.row.original.dateOfCourse, false, false, false)} -{" "}
            {formatDate(info.row.original.endOfCourse)}
          </span>
        ),
      },
      {
        id: "numberOfStudents",
        accessorKey: "numberOfStudents",
        header: "Kursanci",
        size: 90,
        enableColumnFilter: false,
        cell: (info) => {
          const count = info.getValue<number>();
          return (
            <Link
              to={routes.COURSES_MANAGE_NESTED.STUDENTS(info.row.original.id)}
              onClick={(e) => e.stopPropagation()}
              className="text-md text-center font-semibold hover:underline"
            >
              {count}
            </Link>
          );
        },
      },
      {
        id: "todayPrice",
        accessorKey: "todayPrice",
        header: "Cena",
        cell: (info) => toMoneyFormat(info.row.original.todayPrice, true),
        enableColumnFilter: false,
      },

      {
        id: "status",
        accessorKey: "status",
        enableColumnFilter: true,
        header: "Status",
        filterFn: "equals",
        size: 150,
        cell: (info) => <CourseBadgeStatus status={info.getValue<CourseStatus>()} />,
        meta: {
          filterVariant: "select",
          options: COURSE_STATUS_OPTIONS,
        },
      },
      {
        id: "publicDataReady",
        accessorKey: "publicDataReady",
        header: "Stan",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => <CourseFlags {...info.row.original} courseId={info.row.original.id} />,
      },
      {
        id: "actions",
        accessorKey: "actions",
        header: "Akcje",
        size: 90,
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => <CoursesTableActions courseId={info.row.original.id} onDuplicate={setDuplicateCourseId} />,
      },
    ],
    [],
  );
  if (isLoading) return <PaziaLoader position="center" />;
  return (
    <>
      <TanTableClient
        data={courses.map((course) => ({
          ...course,
          status: course.status + "",
        }))}
        columns={columns}
        hideBorder
        onRowClick={(r) => navigate(routes.COURSES_MANAGE(r.id))}
        filterStorageKey={filterStorageKey}
        onClearFilters={() => {
          // Filter storage is cleared in the TanTableClient component
        }}
      />
      <DuplicateCourseModal
        courseId={duplicateCourseId}
        onCloseRequest={() => {
          setDuplicateCourseId(null);
        }}
        onSuccessDuplicate={invalidate}
      />
      <AdminResourceDeleteModal
        title="Usuń edycję kursu"
        description="Jesteś pewien że chcesz usunąć edycję kursu?"
        onSuccessDelete={invalidate}
        deleteAction={coursesApi.delete}
      />
    </>
  );
};

const CourseGroupBaseTab = ({ courseGroup, invalidate }: { courseGroup: CourseGroupApi; invalidate: () => void }) => {
  const { showSuccess, showError } = useToaster();
  const onSubmit = async (data: CourseGroupFormSchema) => {
    try {
      await coursegroupsApi.update(courseGroup.id, data);
      showSuccess("Zaktualizowano grupę kursów");
    } catch (error) {
      showError(getApiErrorMessage(error, "Nie udało się zaktualizować grupy kursów"));
    } finally {
      invalidate();
    }
  };

  return <CourseGroupBaseForm courseGroup={courseGroup} onSubmit={onSubmit} />;
};
const Tabs = ({ courseGroupId }: { courseGroupId: string }) => {
  const checkLocationPathName = (path: string) => {
    return removeTrailingSlash(location.pathname) === removeTrailingSlash(path);
  };
  const tabs = [
    {
      name: "Kursy",
      path: routes.COURSE_GROUPS_EDIT_COURSES(courseGroupId),
      active: checkLocationPathName(routes.COURSE_GROUPS_EDIT_COURSES(courseGroupId)),
      link: routes.COURSE_GROUPS_EDIT_COURSES(courseGroupId),
    },
    {
      name: "Dane podstawowe",
      path: routes.COURSE_GROUPS_EDIT(courseGroupId),
      active: checkLocationPathName(routes.COURSE_GROUPS_EDIT(courseGroupId)),
      link: routes.COURSE_GROUPS_EDIT(courseGroupId),
    },
    {
      name: "Kontakt",
      path: routes.COURSE_GROUPS_EDIT_CONTACT(courseGroupId),
      active: checkLocationPathName(routes.COURSE_GROUPS_EDIT_CONTACT(courseGroupId)),
      link: routes.COURSE_GROUPS_EDIT_CONTACT(courseGroupId),
    },
    {
      name: "Informacje",
      path: routes.COURSE_GROUPS_EDIT_PREVIEW(courseGroupId),
      active: checkLocationPathName(routes.COURSE_GROUPS_EDIT_PREVIEW(courseGroupId)),
      link: routes.COURSE_GROUPS_EDIT_PREVIEW(courseGroupId),
    },
    {
      name: "Szczegółowe informacje",
      path: routes.COURSE_GROUPS_EDIT_DETAILED_INFO(courseGroupId),
      active: checkLocationPathName(routes.COURSE_GROUPS_EDIT_DETAILED_INFO(courseGroupId)),
      link: routes.COURSE_GROUPS_EDIT_DETAILED_INFO(courseGroupId),
    },
    {
      name: "Program",
      path: routes.COURSE_GROUPS_EDIT_AGENDA(courseGroupId),
      active: checkLocationPathName(routes.COURSE_GROUPS_EDIT_AGENDA(courseGroupId)),
      link: routes.COURSE_GROUPS_EDIT_AGENDA(courseGroupId),
    },
    {
      name: "FAQ",
      path: routes.COURSE_GROUPS_EDIT_FAQ(courseGroupId),
      active: checkLocationPathName(routes.COURSE_GROUPS_EDIT_FAQ(courseGroupId)),
      link: routes.COURSE_GROUPS_EDIT_FAQ(courseGroupId),
    },
    {
      name: "Atrybuty",
      path: routes.COURSE_GROUPS_EDIT_ATTRIBUTES(courseGroupId),
      active: checkLocationPathName(routes.COURSE_GROUPS_EDIT_ATTRIBUTES(courseGroupId)),
      link: routes.COURSE_GROUPS_EDIT_ATTRIBUTES(courseGroupId),
    },
    {
      name: "Lista oczekujących",
      path: routes.COURSE_GROUPS_EDIT_WAITLIST(courseGroupId),
      active: checkLocationPathName(routes.COURSE_GROUPS_EDIT_WAITLIST(courseGroupId)),
      link: routes.COURSE_GROUPS_EDIT_WAITLIST(courseGroupId),
    },
  ];
  return <PaziaTabs tabs={tabs} className="mb-4" />;
};
